import React from 'react'
import {useQuery} from '@tanstack/react-query'
import {useTranslations} from 'next-intl'
import {css} from '@emotion/react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Radio,
  Typography
} from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

import Button from '../../../../components/elements/Button'
import BasicDialog from '../../../../components/elements/BasicDialog'
import {useAppCtx} from '../../../../contexts/appCtx'

export default function OptionsCollectionDialog({
                                                  open,
                                                  onClose,
                                                  defaultOptions,
                                                  onOptionsChange,
                                                }) {

  const styles = {
    description: css`
      padding: 16px;
      font-size: 1.2rem;
    `,
    actions: css`
      display: flex;
      justify-content: space-between;
      gap: 8px;
    `,
  }

  const t = useTranslations()

  const [selectedID, setSelectedID] = React.useState(null)
  const onChange = (event) => {
    setSelectedID(event.target.value)
  }

  const {locale} = useAppCtx()

  const {data: collections, isLoading} = useQuery({
    queryKey: ['draw-lots', 'collections', locale],
    queryFn: async () => {
      const resp = await fetch(`/api/draw-lots/collections?locale=${locale}`)
      return await resp.json()
    },
    initialData: [],
  })

  const onConfirm = () => {
    collections.forEach(collection => {
      if (collection.id === selectedID) {
        onOptionsChange(collection.options)
      }
    })
    onClose()
  }

  return (
    <BasicDialog
      autoFullScreen={true}
      title={t('draw-lots.options-collection-dialog.title')}
      open={open}
      onClose={onClose}
      actions={(
        <div css={styles.actions}>
          <div/>
          <div css={css`
            display: flex;
            gap: 8px;
          `}>
            <Button secondary onClick={onClose}>{t('draw-lots.options-collection-dialog.cancel')}</Button>
            <Button disabled={!selectedID}
                    onClick={onConfirm}>{t('draw-lots.options-collection-dialog.confirm')}</Button>
          </div>
        </div>
      )}
    >
      <div>
        <div css={styles.description}>
          <div>{t('draw-lots.options-collection-dialog.description')}</div>
        </div>
        <div>
          {collections.map(collection => (
            <Paper
              key={collection.id}
              css={css`
                margin-top: 2px;
                min-height: 56px;
                min-width: 360px;

                padding: 8px;

                display: flex;
                align-items: center;
              `}
            >
              <Radio
                checked={selectedID === collection.id}
                onChange={onChange}
                value={collection.id}
              />

              <Divider
                css={css`
                  margin: 8px;
                `}
                orientation="vertical"
                flexItem/>

              <div css={css`
                flex: 1;

                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
              `}>
                <Typography
                  css={css`
                    font-size: 1.2em;
                    font-weight: 600;

                    flex: 1;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                  `}
                >{collection.name}</Typography>
                <Accordion
                  css={css`
                    flex: 1;
                    align-self: stretch;
                  `}
                >
                  <AccordionSummary expandIcon={<ArrowDownwardIcon/>}>
                    <Typography
                      css={css`
                        position: absolute;
                        top: 12px;
                        left: 16px;
                        right: 32px;

                        margin-right: 16px;

                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                      `}
                    >{collection.options.map(option => option.name).join(', ')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {collection.options.map(option => (
                        <ListItem
                          key={option.id}
                          css={css`
                            margin-top: 2px;
                            height: 56px;
                            gap: 8px;
                          `}
                          component={Paper}
                        >
                          <div
                            css={css`
                              min-width: 32px;
                              min-height: 32px;
                              width: 32px;
                              height: 32px;
                              background: ${option.color};
                            `}
                          />
                          <ListItemText>
                            {option.name}
                          </ListItemText>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </div>
            </Paper>
          ))}
        </div>
      </div>
    </BasicDialog>
  )
}
